import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Button,
  TextField,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import i18n from "../i18n";
import useIsMobile from "../hooks/useIsMobile";
export default function MealPopUp({
  open,
  meal,
  handleClose,
  cart,
  addToCart,
  updateQty,
  removeFromCart,
  handleQuantityChange,
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const existing = React.useMemo(() => {
    if (!meal) return null; // Handle null meal inside the hook
    return cart.find((item) => item.id === meal.id);
  }, [cart, meal]);

  const count = React.useMemo(() => (existing ? existing.qty : 0), [existing]);

  if (!meal) return null; // Safe to check after all hooks

  const direction = i18n.dir(i18n.language); // <-- proper usage

  //   console.log("show to cart", existing);
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      dir={direction}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 16px 48px rgba(15, 77, 40, 0.18)",
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        position="relative"
        sx={{ bgcolor: "#f3faf5", borderBottom: "1px solid rgba(27,122,61,0.1)" }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            flexGrow: 1,
            fontSize: "clamp(1.3rem, 1.5vw, 1.8rem)",
            fontWeight: 700,
            color: "#0f4d28",
            fontFamily: "Tajawal, Poppins, sans-serif",
          }}
        >
          {meal.name}
        </DialogTitle>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            [direction === "rtl" ? "right" : "left"]: 8,
          }}
        >
          <HighlightOffIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          textAlign: direction === "rtl" ? "right" : "left",
          gap: 2,
          pt: 2.5,
        }}
      >
        <Box textAlign="center" mt={isMobile ? 0 : 1}>
          <img
            src={meal.imageUrl}
            alt={meal.name}
            style={{
              width: "100%",
              borderRadius: "14px",
              objectFit: "cover",
              maxHeight: isMobile ? 220 : 280,
            }}
          />
        </Box>
        <Box minWidth="55%" px={2}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              mb: 2,
              justifyContent: direction === "rtl" ? "flex-start" : "flex-start",
            }}
          >
            <span className="dc-chip">
              {t("calories")} {meal.calories}
            </span>
            <span className="dc-chip">
              {t("protein")} {meal.protein}
              {t("gram") ? ` ${t("gram")}` : "g"}
            </span>
            <span className="dc-chip">
              {t("carbs")} {meal.carbs}
              {t("gram") ? ` ${t("gram")}` : "g"}
            </span>
            <span className="dc-chip">
              {t("fats")} {meal.fat}
              {t("gram") ? ` ${t("gram")}` : "g"}
            </span>
          </Box>
          <Typography
            variant="h5"
            align={direction === "rtl" ? "right" : "left"}
            sx={{ fontWeight: "bold", color: "#0f4d28" }}
          >
            {t("ingredients")}
          </Typography>
          <ul
            style={{ paddingRight: direction === "rtl" ? "1rem" : 0 }}
            align={direction === "rtl" ? "right" : "left"}
          >
            {meal.ingredients.map((ing, idx) => (
              <li key={idx}>
                <Typography>{ing}</Typography>
              </li>
            ))}
          </ul>
          <Typography
            variant="body1"
            mb={2}
            align={direction === "rtl" ? "right" : "left"}
            sx={{ fontWeight: "bold" }}
          >
            {t("rate")} {meal.price}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 2,
              minHeight:"40px",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: "clamp(0.8rem, 0.9vw, 1.3rem)",
                direction: "rtl",
                fontFamily: "Tajawal",
              }}
            >
              {t("quantity")}
            </Typography>
            <TextField
              type="number"
              variant="standard"
              value={count}
              disabled={count === 0}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              onChange={(e) => handleQuantityChange(meal, e.target.value)}
              inputProps={{
                min: 0,
                style: {
                  fontSize: "clamp(1rem, 1vw, 1.4rem)",
                  textAlign: "center",
                  width: "30px",
                  padding: "0 4px",
                },
              }}
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                  {
                    "-webkit-appearance": "none",
                    margin: 0,
                  },
                "& input[type=number]": {
                  "-moz-appearance": "textfield",
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "rgba(0, 0, 0, 0.42)",
                  borderBottom:
                    count === 0 ? 0 : `1px solid rgba(0, 0, 0, 0.42)`,
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "rgba(0, 0, 0, 0.87)",
                },
              }}
            />
            {existing && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  removeFromCart(meal.id);
                }}
                color="error"
                sx={{
                  color: "#c62828",
                  "&:hover": {
                    backgroundColor: "#f9d6d5",
                  },
                  ml: "auto",
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </DialogContent>
      {!existing ? (
        <Button
          className="dc-btn-primary"
          sx={{ width: "100%", minHeight: "52px", borderRadius: 0 }}
          onClick={() => {
            addToCart(meal);
          }}
        >
          {t("addToCart")}
        </Button>
      ) : (
        <Box display="flex" justifyContent="center" minHeight="52px">
          <Button
            className="dc-btn-soft"
            sx={{ width: "50%", borderRadius: 0, minHeight: "52px" }}
            onClick={(e) => {
              e.stopPropagation();
              const existing = cart.find((item) => item.id === meal.id);
              if (existing) {
                updateQty(meal.id, existing.qty + 1);
              } else {
                addToCart(meal);
              }
            }}
          >
            +
          </Button>
          <Button
            sx={{
              width: "50%",
              backgroundColor: "#fdecea",
              color: "#c62828",
              justifyContent: "center",
              fontFamily: "Tajawal",
              borderRadius: 0,
              minHeight: "52px",
              fontWeight: 700,
            }}
            onClick={(e) => {
              e.stopPropagation();
              const existing = cart.find((item) => item.id === meal.id);
              if (existing) {
                if (existing.qty === 1) {
                  removeFromCart(existing.id);
                } else {
                  updateQty(existing.id, existing.qty - 1);
                }
              }
            }}
          >
            -
          </Button>
        </Box>
      )}
    </Dialog>
  );
}
